import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import type { TeamMemberScore } from "../../TeamcompositeResults/api/team.api";

const AXIS_LABELS = [
  "Independence",
  "Advancement",
  "Exploring",
  "Caring",
  "Collaboration",
  "Safety",
];

// Maps each axis label to its score key in the API response
const AXIS_KEYS = [
  "Independence",
  "Advancement",
  "Exploring",
  "Caring",
  "Collaboration",
  "Safety",
];

const MEMBER_COLORS = [
  "#5b5fc7",
  "#e74c3c",
  "#2ecc71",
  "#f39c12",
  "#1abc9c",
  "#9b59b6",
  "#e67e22",
  "#3498db",
  "#e91e63",
  "#00bcd4",
  "#ff5722",
  "#4caf50",
  "#795548",
  "#607d8b",
  "#ff9800",
];

// Absolute positioning for each axis label around the chart
const LABEL_POSITIONS = [
  "top-0 left-1/2 -translate-x-1/2",     // Independence (top)
  "top-[18%] right-0",                    // Advancement (top-right)
  "bottom-[18%] right-[5%]",             // Exploring (bottom-right)
  "bottom-0 left-1/2 -translate-x-1/2", // Caring (bottom)
  "bottom-[18%] left-[5%]",             // Collaboration (bottom-left)
  "top-[18%] left-0",                    // Safety (top-left)
];

const TOOLTIP_STYLE: React.CSSProperties[] = [
  { top: "100%", left: "50%", transform: "translateX(-50%)", marginTop: 6 },
  { top: 0, right: "100%", marginRight: 6 },
  { bottom: 0, right: "100%", marginRight: 6 },
  { bottom: "100%", left: "50%", transform: "translateX(-50%)", marginBottom: 6 },
  { bottom: 0, left: "100%", marginLeft: 6 },
  { top: 0, left: "100%", marginLeft: 6 },
];

interface MappedMember {
  id: number;
  name: string;
  image?: string;
  scores: number[];
}

interface Props {
  apiMembers?: TeamMemberScore[];
  isPrint?: boolean;
}

const TeamSpiderGraph: React.FC<Props> = ({
  apiMembers = [],
  isPrint = false,
}) => {
  const [hoveredLabel, setHoveredLabel] = useState<number | null>(null);

  const members: MappedMember[] = apiMembers.map((m) => ({
    id: m.user_id,
    name: `${m.first_name} ${m.last_name}`,
    image: m.image,
    scores: AXIS_KEYS.map((key) => m.scores[key] ?? 0),
  }));

  if (members.length === 0) {
    return (
      <div className="flex items-center justify-center h-[280px] text-[#9aa8d4] text-sm">
        No team member data available
      </div>
    );
  }

  const capped = members.slice(0, 15);
  const colors = capped.map((_, i) => MEMBER_COLORS[i % MEMBER_COLORS.length]);
  const series = capped.map((m) => ({ name: m.name, data: m.scores }));

  const options: any = {
    chart: {
      type: "radar",
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors,
    stroke: { width: 2, colors },
    fill: { opacity: 0 },
    markers: { size: 0 },
    xaxis: {
      categories: AXIS_LABELS,
      labels: { show: false },
    },
    yaxis: { min: 0, max: 100, show: false },
    legend: { show: false },
    tooltip: {
      enabled: !isPrint,
      shared: false,
      custom: ({ series: s, seriesIndex, dataPointIndex }: any) => {
        if (seriesIndex == null || seriesIndex < 0 || seriesIndex >= capped.length) {
          return "";
        }
        const member = capped[seriesIndex];
        const category = AXIS_LABELS[dataPointIndex];
        const score = s[seriesIndex]?.[dataPointIndex] ?? 0;
        const color = colors[seriesIndex];

        const avatar = member.image
          ? `<img 
        src="${member.image}" 
        alt="${member.name}" 
        style="
          width:20px;
          height:20px;
          border-radius:50%;
          object-fit:cover;
          border:1px solid ${color};
          flex-shrink:0;
        "
     />`
          : `<div style="
        width:20px;
        height:20px;
        border-radius:50%;
        background:${color};
        display:flex;
        align-items:center;
        justify-content:center;
        color:#fff;
        font-size:9px;
        font-weight:700;
        flex-shrink:0;
      ">
        ${member.name.charAt(0)}
     </div>`;

        return `
          <div style="padding:10px 14px;background:#26295e;border-radius:8px;min-width:160px;box-shadow:0 4px 12px rgba(0,0,0,0.25);">
            <div style="font-weight:700;margin-bottom:8px;font-size:12px;color:#9aa8d4;text-transform:uppercase;letter-spacing:0.05em;">${category}</div>
            <div style="display:flex;align-items:center;gap:8px;">
              ${avatar}
              <div>
                <div style="color:#fff;font-size:12px;font-weight:600;">${member.name}</div>
                <div style="color:#fff;font-size:18px;font-weight:700;line-height:1.2;">${score}<span style="font-size:12px;font-weight:400;">%</span></div>
              </div>
            </div>
          </div>
        `;
      },
    },
  };

  return (
    <div className="w-full">
      <div className="relative w-full max-w-[360px] mx-auto">
        <ReactApexChart
          options={options}
          series={series}
          type="radar"
          height={280}
        />

        <div className="absolute inset-0 pointer-events-none">
          {AXIS_LABELS.map((label, i) => (
            <div
              key={label}
              className={`absolute ${LABEL_POSITIONS[i]} pointer-events-auto`}
            >
              <span
                className={`text-[11px] font-semibold text-[#3b519c] whitespace-nowrap ${isPrint ? "" : "cursor-default"}`}
                onMouseEnter={() => !isPrint && setHoveredLabel(i)}
                onMouseLeave={() => !isPrint && setHoveredLabel(null)}
              >
                {label}
              </span>

              {!isPrint && hoveredLabel === i && (
                <div
                  className="absolute z-30 bg-[#26295e] rounded-lg shadow-xl p-3 min-w-[160px] pointer-events-none"
                  style={TOOLTIP_STYLE[i]}
                >
                  <div className="text-[11px] font-bold text-[#9aa8d4] uppercase tracking-wide mb-2">
                    {label}
                  </div>
                  {capped.map((m, mi) => (
                    <div key={m.id} className="flex items-center gap-2 mb-1.5">
                      {m.image ? (
                        <img
                          src={m.image}
                          alt={m.name}
                          className="w-5 h-5 rounded-full object-cover border"
                          style={{ borderColor: colors[i] }}
                        />
                      ) : (
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0"
                          style={{ backgroundColor: colors[i] }}
                        >
                          {m.name.charAt(0)}
                        </div>
                      )}
                      <span className="text-white text-[11px]">
                        {m.name}:{" "}
                        <span className="font-bold">{m.scores[i]}%</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center mt-4 px-4">
        {capped.map((m, i) => (
          <div key={m.id} className="flex items-center gap-2">
            <div
              className="w-4 h-[2px] rounded-full flex-shrink-0"
              style={{ backgroundColor: colors[i] }}
            />
            {m.image ? (
              <img
                src={m.image}
                alt={m.name}
                className="w-5 h-5 rounded-full object-cover border flex-shrink-0"
                style={{ borderColor: colors[i] }}
              />
            ) : (
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0"
                style={{ backgroundColor: colors[i] }}
              >
                {m.name.charAt(0)}
              </div>
            )}
            <span className="text-[11px] text-[#26295e] whitespace-nowrap">
              {m.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamSpiderGraph;
